<template>
  <!-- The Invoices screen as the app draws it today (Workspace369 origin/main, Oct 22 2026), at the
       app's own scale. Wide containers get the desktop shell: SidebarV2 (sidebar_v2_menu.dart), the
       canvas header, and invoices_list_screen.dart's toolbar (search, Import data, New Invoice), mode
       tabs (invoice_list_mode_switch.dart), filter row, Invoice Money Watch (money_watch_list_section.dart)
       and the table in its default columns (invoice_filter_controller.dart). Below 600px it is the
       phone layout the app switches to under 850px: Add button, search, Invoices | Recurring, the
       filter rail, Money Watch and invoice cards (invoice_card_widget.dart: stripe, "Invoice <number>",
       facts with shorten() amounts, footer actions), over the default bottom bar
       (bottom_nav_config_model.dart). Labels are app_en.arb strings; the Northstar Studio workspace,
       clients and amounts are sample data. The top invoice walks draft → open → partial → paid; the
       server renders `paid`. -->
  <div class="iaw" :class="`iaw--${state}`" aria-hidden="true">
    <svg class="iaw-sprite" width="0" height="0">
      <symbol v-for="name in used" :id="`iaw-${name}`" :key="name" viewBox="0 0 24 24"><path :d="appIcons[name]" /></symbol>
    </svg>

    <!-- Desktop and tablet -->
    <div class="iaw-desk">
      <aside class="iaw-side">
        <div class="iaw-side-top"><svg class="ic"><use href="#iaw-view_sidebar" /></svg></div>
        <div class="iaw-ws"><span class="iaw-logo">N</span><span class="iaw-ell">Northstar Studio</span><svg class="ic sm"><use href="#iaw-unfold_more" /></svg></div>
        <nav class="iaw-nav">
          <template v-for="g in nav" :key="g.k">
            <div class="iaw-ni top">
              <svg class="ic"><use :href="`#iaw-${g.i}`" /></svg><span>{{ g.k }}</span>
              <svg v-if="g.kids || g.chev" class="ic sm iaw-chev"><use :href="`#iaw-${g.open ? 'expand_more' : 'chevron_right'}`" /></svg>
            </div>
            <template v-if="g.open">
              <div v-for="c in g.kids" :key="c" class="iaw-ni child" :class="{ sel: c === 'Invoices' }"><span>{{ c }}</span></div>
            </template>
          </template>
        </nav>
        <div class="iaw-side-foot">
          <div v-for="f in foot" :key="f.k" class="iaw-ni"><svg class="ic"><use :href="`#iaw-${f.i}`" /></svg><span>{{ f.k }}</span></div>
        </div>
      </aside>

      <div class="iaw-main">
        <header class="iaw-head">
          <span class="iaw-title"><svg class="ic sm"><use href="#iaw-inventory_2" /></svg>Invoices</span>
          <span class="iaw-hsearch"><svg class="ic sm"><use href="#iaw-search" /></svg>Search...<kbd>⌘K</kbd></span>
          <span class="iaw-icbtn iaw-bell"><svg class="ic"><use href="#iaw-notifications" /></svg><b>3</b></span>
          <span class="iaw-icbtn"><svg class="ic"><use href="#iaw-settings" /></svg></span>
          <span class="iaw-icbtn"><svg class="ic"><use href="#iaw-chat_bubble_outline" /></svg></span>
        </header>

        <section class="iaw-sheet">
          <div class="iaw-toolbar">
            <span class="iaw-search"><svg class="ic"><use href="#iaw-search" /></svg>Search Invoice or PO#</span>
            <span class="iaw-btn sec"><svg class="ic"><use href="#iaw-cloud_upload" /></svg>Import data</span>
            <span class="iaw-btn"><svg class="ic"><use href="#iaw-add" /></svg>New Invoice</span>
          </div>
          <div class="iaw-tabs">
            <span class="on">All invoices</span><span>Drafts</span><span><svg class="ic xs"><use href="#iaw-autorenew" /></svg>Recurring</span><span>Overdue</span>
          </div>
          <div class="iaw-filters">
            <span class="iaw-chip"><svg class="ic xs"><use href="#iaw-swap_vert" /></svg>Sort<svg class="ic xs"><use href="#iaw-expand_more" /></svg></span>
            <span class="iaw-chip"><svg class="ic xs"><use href="#iaw-add" /></svg>Filter</span>
            <span class="iaw-grow" />
            <i class="iaw-divider" />
            <span class="iaw-chip"><svg class="ic xs"><use href="#iaw-view_module" /></svg>View<svg class="ic xs"><use href="#iaw-expand_more" /></svg></span>
            <span class="iaw-chip"><svg class="ic xs"><use href="#iaw-download" /></svg>Export</span>
            <span class="iaw-chip"><svg class="ic xs"><use href="#iaw-analytics" /></svg>Analyze</span>
            <span class="iaw-chip"><svg class="ic xs"><use href="#iaw-view_column" /></svg>Columns</span>
          </div>
          <div class="iaw-watch">
            <svg class="ic"><use href="#iaw-receipt_long" /></svg>
            <b>Invoice Money Watch</b>
            <span class="iaw-watch-cap">$14,190.00 overdue · 3 invoices</span>
            <span class="iaw-grow" />
            <svg class="ic"><use href="#iaw-chevron_right" /></svg>
          </div>
          <div class="iaw-table">
            <table>
              <colgroup><col class="w-ck"><col class="w-amt"><col class="w-money"><col class="w-money"><col class="w-no c-no"><col class="w-title c-title"><col class="w-cust"><col class="w-email"><col class="w-date"></colgroup>
              <thead>
                <tr>
                  <th class="ck-cell"><span class="iaw-ck" /></th>
                  <th v-for="c in columns" :key="c.k" :class="[{ r: c.r }, c.cls]"><svg class="ic xs"><use :href="`#iaw-${c.i}`" /></svg>{{ c.k }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in rows" :key="row.no" :class="{ 'is-focus': row.focus }">
                  <td class="ck-cell"><span class="iaw-ck" /></td>
                  <td><span class="iaw-amt"><b>{{ row.amount }}</b><em class="iaw-tag" :class="row.tone">{{ row.status }}</em></span></td>
                  <td class="r"><b>{{ row.due }}</b><small v-if="row.late" class="late">{{ row.late }}</small></td>
                  <td class="r"><span :class="{ 'iaw-solo': row.focus && !row.paidOn }">{{ row.paid }}</span><small v-if="row.paidOn || row.focus" :class="{ 'iaw-ghost': !row.paidOn }">{{ row.paidOn || "\u00a0" }}</small></td>
                  <td class="c-no">{{ row.no }}</td>
                  <td class="c-title">{{ row.title }}</td>
                  <td class="cust">{{ row.client }}</td>
                  <td class="muted">{{ row.email }}</td>
                  <td :class="row.late ? 'late' : 'muted'">{{ row.dueDate }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <footer class="iaw-foot">
            <span>8 items</span><span class="iaw-grow" />
            <span class="iaw-chip"><svg class="ic xs"><use href="#iaw-view_module" /></svg>20 rows<svg class="ic xs"><use href="#iaw-expand_more" /></svg></span>
            <span class="iaw-chip">Previous</span><span class="muted">1 / 1</span><span class="iaw-chip">Next</span>
          </footer>
        </section>
      </div>
    </div>

    <!-- Phone -->
    <div class="iaw-phone">
      <div class="iap-bar">
        <span class="iap-icbtn"><svg class="ic"><use href="#iaw-search" /></svg></span>
        <span class="iap-add"><svg class="ic"><use href="#iaw-add" /></svg>Add</span>
      </div>
      <div class="iap-body">
        <span class="iap-search"><svg class="ic"><use href="#iaw-search" /></svg>Search Invoice or PO#</span>
        <div class="iap-seg"><span class="on">Invoices</span><span><svg class="ic"><use href="#iaw-autorenew" /></svg>Recurring</span></div>
        <div class="iap-rail">
          <span class="iap-chip"><svg class="ic"><use href="#iaw-swap_vert" /></svg>Sort</span>
          <span class="iap-chip"><svg class="ic"><use href="#iaw-add" /></svg>Filter</span>
          <span class="iap-more"><svg class="ic"><use href="#iaw-more_horiz" /></svg></span>
        </div>
        <div class="iap-watch">
          <svg class="ic"><use href="#iaw-receipt_long" /></svg>
          <span><b>Invoice Money Watch</b><em>$14,190.00 overdue · 3 invoices</em></span>
          <svg class="ic"><use href="#iaw-chevron_right" /></svg>
        </div>
        <article v-for="card in cards" :key="card.no" class="iap-card" :class="{ 'is-focus': card.focus, 'is-paid': card.tone === 'green' }" :style="{ '--stripe': card.stripe }">
          <div class="iap-card-in">
            <div class="iap-card-head">
              <b>Invoice {{ card.no }}</b>
              <span class="iap-acts"><em class="iaw-tag" :class="card.tone">{{ card.status }}</em><span class="iap-dots"><svg class="ic"><use href="#iaw-more_vert" /></svg></span></span>
            </div>
            <p class="iap-client">{{ card.client }}</p>
            <div class="iap-factsets">
              <dl v-for="set in card.sets" :key="set.k" class="iap-facts" :class="{ 'is-off': !set.on }" :aria-hidden="set.on ? undefined : 'true'">
                <div v-for="f in set.facts" :key="f.l"><dt>{{ f.l }}</dt><dd :class="f.a">{{ f.v }}</dd></div>
              </dl>
            </div>
          </div>
          <div class="iap-card-foot">
            <span class="iap-opened"><svg class="ic"><use href="#iaw-visibility" /></svg>Last opened: <b>{{ card.opened }}</b></span>
            <span class="iap-btns">
              <span class="iap-btn"><svg class="ic"><use href="#iaw-history_edu" /></svg>Audit history</span>
              <span class="iap-btn primary"><svg class="ic"><use :href="card.tone === 'green' ? '#iaw-receipt_long' : '#iaw-check_circle_outline'" /></svg>{{ card.tone === "green" ? "View receipt" : "Mark as paid" }}</span>
            </span>
          </div>
        </article>
      </div>
      <nav class="iap-nav">
        <span v-for="t in tabs" :key="t.k" :class="{ on: t.k === 'Invoices' }"><svg class="ic"><use :href="`#iaw-${t.i}`" /></svg>{{ t.k }}</span>
        <span class="iap-navmore"><svg class="ic"><use href="#iaw-add" /></svg></span>
      </nav>
    </div>
  </div>
</template>

<script setup>
import { appIcons } from "~/data/app-icons.js";

const props = defineProps({
  // draft | open | partial | paid
  state: { type: String, default: "paid" },
});

const used = ["account_balance_wallet", "add", "analytics", "attach_money", "auto_awesome", "autorenew", "calendar_today", "chat_bubble_outline", "check_circle_outline", "chevron_right", "cloud_upload", "dashboard", "description", "download", "event", "expand_more", "forum", "handyman", "history_edu", "inventory_2", "logout", "mail", "more_horiz", "more_vert", "notifications", "payments", "people_alt", "person", "receipt_long", "search", "settings", "support_agent", "swap_vert", "tag", "unfold_more", "view_column", "view_module", "view_sidebar", "visibility", "work_outline", "workspaces"];

const nav = [
  { k: "Dashboard", i: "dashboard" },
  { k: "AI AutoPilot", i: "auto_awesome", chev: true },
  { k: "Clients", i: "people_alt", kids: ["Clients", "Requests"] },
  { k: "Work", i: "work_outline", kids: ["Projects", "Calendar", "Tasks", "Reminders", "Time Tracking"] },
  { k: "Finance", i: "account_balance_wallet", open: true, kids: ["Proposals", "Contracts", "Invoices", "Retainers", "Expenses", "Accounting", "Product Catalog"] },
  { k: "Communications", i: "forum", kids: ["Inbox", "Emails", "Mobile", "Campaigns", "Workflows", "Funnels"] },
  { k: "Tools", i: "handyman", kids: ["Notes", "Files", "Habits"] },
];
const foot = [
  { k: "Settings", i: "settings" },
  { k: "Support", i: "support_agent" },
  { k: "Log Out", i: "logout" },
];
const columns = [
  { k: "Amount", i: "attach_money" },
  { k: "Due", i: "account_balance_wallet", r: true },
  { k: "Paid", i: "payments", r: true },
  { k: "Invoice #", i: "tag", cls: "c-no" },
  { k: "Title", i: "description", cls: "c-title" },
  { k: "Customer name", i: "person" },
  { k: "Customer email", i: "mail" },
  { k: "Due date", i: "event" },
];
const tabs = [
  { k: "Home", i: "dashboard" },
  { k: "Projects", i: "workspaces" },
  { k: "AI AutoPilot", i: "auto_awesome" },
  { k: "Invoices", i: "receipt_long" },
  { k: "Calendar", i: "calendar_today" },
];

// The invoice that moves: its status, balance due and paid amount for each state.
const top = {
  draft: { status: "Draft", tone: "gray", due: "$4,800.00", paid: "$0.00", stripe: "#6b7c93", opened: "Not opened" },
  open: { status: "Open", tone: "blue", due: "$4,800.00", paid: "$0.00", stripe: "#635bff", opened: "Oct 14" },
  partial: { status: "Partial", tone: "amber", due: "$2,800.00", paid: "$2,000.00", paidOn: "Oct 15", stripe: "#b7791f", opened: "Oct 15" },
  paid: { status: "Paid", tone: "green", due: "$0.00", paid: "$4,800.00", paidOn: "Oct 15", stripe: "", opened: "Oct 15" },
};
const focus = computed(() => top[props.state] || top.paid);

const rows = computed(() => [
  { no: "1034", amount: "$4,800.00", title: "Brand identity deposit (40%)", client: "Marlowe Coffee Co.", email: "ava@marlowecoffee.example", dueDate: "Oct 17", focus: true, ...focus.value },
  { no: "1033", amount: "$1,300.00", status: "Past due", tone: "red", due: "$1,300.00", late: "3 days overdue", paid: "$0.00", title: "Seasonal menu design", client: "Juniper Bakery", email: "lena@juniperbakery.example", dueDate: "Oct 12" },
  { no: "1031", amount: "$9,690.00", status: "Past due", tone: "red", due: "$9,690.00", late: "12 days overdue", paid: "$0.00", title: "Brand refresh, phase 2", client: "Harbor & Co.", email: "hannah@harborco.example", dueDate: "Oct 3" },
  { no: "1029", amount: "$3,200.00", status: "Past due", tone: "red", due: "$3,200.00", late: "6 days overdue", paid: "$0.00", title: "Portal scope, milestone 1", client: "Atlas Logistics", email: "marcus@atlaslogistics.example", dueDate: "Oct 9" },
  { no: "1027", amount: "$3,560.00", status: "Partial", tone: "amber", due: "$1,780.00", paid: "$1,780.00", paidOn: "Oct 8", title: "Dental rebrand, phase 1", client: "Brightside Dental", email: "daniel@brightsidedental.example", dueDate: "Oct 24" },
  { no: "1025", amount: "$7,120.00", status: "Paid", tone: "green", due: "$0.00", paid: "$7,120.00", paidOn: "Oct 5", title: "Dental rebrand, discovery", client: "Brightside Dental", email: "daniel@brightsidedental.example", dueDate: "Oct 6" },
  { no: "1024", amount: "$4,200.00", status: "Paid", tone: "green", due: "$0.00", paid: "$4,200.00", paidOn: "Sep 30", title: "Website care plan, annual", client: "Orchard Veterinary", email: "priya@orchardvet.example", dueDate: "Oct 1" },
  { no: "1022", amount: "$9,300.00", status: "Paid", tone: "green", due: "$0.00", paid: "$9,300.00", paidOn: "Sep 24", title: "Booking system build", client: "Summit Physio", email: "grace@summitphysio.example", dueDate: "Sep 25" },
]);

// Phone cards: amounts as the card's shorten() writes them, dates as formatTimestampToDate.
// On the phone the card skips Partial (its five facts need a third row), so the card never changes height.
// Both fact layouts are always laid out in one grid cell and only the current one is visible: Paid on
// joining the row would push Creation Date to the next line, a layout shift (motion never moves the page).
const cards = computed(() => {
  const state = props.state === "partial" ? "open" : props.state;
  const f = top[state] || top.paid;
  const total = { l: "Invoice Total", v: "$4.8k" };
  const created = { l: "Creation Date", v: "10/02/2026" };
  const sets = [
    { k: "unpaid", on: state !== "paid", facts: [total, created] },
    { k: "paid", on: state === "paid", facts: [total, { l: "Paid on", v: "10/15/2026", a: "ok" }, created] },
  ];
  return [
    { no: "1034", client: "Marlowe Coffee Co.", focus: true, status: f.status, tone: f.tone, stripe: f.stripe, opened: f.opened, sets },
    { no: "1033", client: "Juniper Bakery", status: "Past due", tone: "red", stripe: "#d92d20", opened: "Oct 6", sets: [{ k: "only", on: true, facts: [{ l: "Invoice Total", v: "$1.3k" }, { l: "Overdue", v: "3 days", a: "bad" }, { l: "Creation Date", v: "09/28/2026" }] }] },
  ];
});
</script>

<style>
/* Tokens: shadcn Neutral (the app's default scheme) blended by AppShellPalette; tags are AppTone pairs. */
.iaw {
  --ink: #0a0a0a; --primary: #171717; --on-primary: #fafafa;
  --fill: #eef1f4; --canvas: #f7f7f7; --page: #fff; --edge: #ebebeb; --card-edge: #e6e6e6; --line2: #efefef;
  --ink2: #4f4f4f; --ink3: #717171; --quiet: #a7a7a7; --selected: rgb(10 10 10 / 0.06); --hover: rgb(10 10 10 / 0.04);
  --gray-t: #5e5e5e; --gray-f: rgb(0 0 0 / 0.067); --green-t: #2d7a4e; --green-f: #e6f4ea; --blue-t: #3f58bf; --blue-f: #ebf0fd;
  --red-t: #ba3a37; --red-f: #fbeaea; --amber-t: #946000; --amber-f: #fdf4c3;
  --card-ink: #0a2540; --card-muted: #6b7c93; --card-line: #e3e8ee; --card-ok: #00a76f; --card-warn: #b7791f; --card-bad: #e53935;
  position: relative;
  container-type: inline-size;
  color: var(--ink);
  font-family: "Space Grotesk", ui-sans-serif, system-ui, sans-serif;
  font-size: 13px;
  font-variant-numeric: tabular-nums;
  line-height: 1.3;
  text-align: left;
}
.dark .iaw {
  --ink: #fafafa; --primary: #fafafa; --on-primary: #171717;
  --fill: #232527; --canvas: #121212; --page: #1a1a1a; --edge: #272727; --card-edge: #2c2c2c; --line2: #252525;
  --ink2: #c2c2c2; --ink3: #9a9a9a; --quiet: #6b6b6b; --selected: rgb(250 250 250 / 0.1); --hover: rgb(250 250 250 / 0.05);
  --gray-t: #bdbdbd; --gray-f: rgb(255 255 255 / 0.12); --green-t: #6ed39a; --green-f: rgb(63 178 110 / 0.18); --blue-t: #93aef9; --blue-f: rgb(91 127 234 / 0.2);
  --red-t: #f69591; --red-f: rgb(229 83 78 / 0.2); --amber-t: #f1c95e; --amber-f: rgb(224 170 36 / 0.17);
  --card-ink: #fafafa; --card-muted: #a3a3a3; --card-line: rgb(82 82 82 / 0.55);
}
/* The window's size is reserved before its markup has streamed in: the browser paints mid-parse, and
   on phones the phone layout comes after the (hidden) desktop markup, so without this the frame
   opens at 0px and pushes the hero copy down (Lighthouse CLS 0.25). Same sizes as the layouts below. */
.iaw { min-height: 600px; }
@media (max-width: 1128px) { .iaw { min-height: 540px; } }
/* 648 = the 600px phone container plus the hero's 24px gutters. */
@media (max-width: 648px) { .iaw { min-height: 0; aspect-ratio: 390 / 700; } }
.iaw-sprite { position: absolute; width: 0; height: 0; overflow: hidden; }
/* display: inline-block undoes Tailwind's preflight (svg { display: block }). */
.iaw .ic { display: inline-block; flex: none; width: 18px; height: 18px; fill: currentColor; vertical-align: middle; }
.iaw .ic.sm { width: 16px; height: 16px; }
.iaw .ic.xs { width: 14px; height: 14px; }
.iaw-grow { flex: 1; }
.iaw-ell { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.iaw .muted { color: var(--ink2); }

/* ---------- desktop ---------- */
.iaw-desk {
  display: grid;
  grid-template-columns: 252px minmax(0, 1fr);
  height: 600px;
  overflow: hidden;
  border: 1px solid var(--card-edge);
  border-radius: 10px;
  background: var(--canvas);
  box-shadow: 0 30px 60px -30px rgb(15 23 42 / 0.28), 0 12px 24px -16px rgb(15 23 42 / 0.12);
  -webkit-mask-image: linear-gradient(#000 90%, transparent);
  mask-image: linear-gradient(#000 90%, transparent);
}
.dark .iaw-desk { border-color: #3a3a3a; box-shadow: inset 0 1px 0 rgb(255 255 255 / 0.06), 0 30px 60px -30px rgb(0 0 0 / 0.7); }
.dark .iaw-phone { border-color: #3a3a3a; }
.iaw-side { display: flex; flex-direction: column; min-height: 0; padding: 0 10px 10px; }
.iaw-side-top { display: flex; align-items: center; height: 48px; padding-left: 6px; color: var(--ink2); }
.iaw-ws { display: flex; align-items: center; gap: 10px; height: 40px; margin-bottom: 6px; border-bottom: 1px solid var(--edge); padding: 0 6px 6px; font-weight: 600; }
.iaw-ws .ic { margin-left: auto; color: var(--ink3); }
.iaw-logo { display: grid; flex: none; place-items: center; width: 26px; height: 26px; border-radius: 6px; background: #171717; color: #fafafa; font-size: 12px; font-weight: 700; }
.dark .iaw-logo { background: #fafafa; color: #171717; }
.iaw-nav { flex: 1; min-height: 0; overflow: hidden; }
.iaw-ni { display: flex; align-items: center; gap: 10px; height: 32px; padding: 0 8px; border-radius: 6px; color: var(--ink2); white-space: nowrap; }
.iaw-ni.top { color: var(--ink); font-weight: 600; }
.iaw-ni.child { padding-left: 38px; }
.iaw-ni.sel { background: var(--selected); color: var(--ink); font-weight: 500; }
.iaw-ni .iaw-chev { margin-left: auto; color: var(--ink3); }
.iaw-side-foot { border-top: 1px solid var(--edge); padding-top: 6px; }
.iaw-side-foot .iaw-ni { height: 30px; }
.iaw-main { display: grid; grid-template-rows: 48px minmax(0, 1fr); min-width: 0; }
.iaw-head { display: flex; align-items: center; gap: 12px; padding: 0 14px 0 4px; }
.iaw-title { display: flex; flex: 1; align-items: center; gap: 8px; font-weight: 600; }
.iaw-title .ic { color: var(--ink2); }
.iaw-hsearch { display: flex; align-items: center; gap: 8px; width: 230px; height: 30px; border: 1px solid var(--card-edge); border-radius: 6px; background: var(--page); padding: 0 6px 0 10px; color: var(--ink3); }
.iaw-hsearch kbd { margin-left: auto; border: 1px solid var(--card-edge); border-radius: 4px; padding: 0 5px; font: inherit; font-size: 11px; }
.iaw-icbtn { position: relative; display: grid; place-items: center; width: 28px; height: 28px; color: var(--ink2); }
.iaw-bell b { position: absolute; top: 0; right: 0; display: grid; place-items: center; min-width: 14px; height: 14px; border-radius: 4px; background: #e53935; color: #fff; font-size: 9px; }
.iaw-sheet { display: flex; flex-direction: column; gap: 10px; min-height: 0; overflow: hidden; border-top: 1px solid var(--edge); border-left: 1px solid var(--edge); border-top-left-radius: 10px; background: var(--page); padding: 12px 0 0 12px; }
.iaw-toolbar { display: flex; gap: 8px; padding-right: 12px; }
.iaw-search { display: flex; flex: 1; align-items: center; gap: 8px; height: 32px; border: 1px solid transparent; background: var(--fill); border-radius: 6px; padding: 0 10px; color: var(--ink3); }
.iaw-btn { display: inline-flex; align-items: center; gap: 6px; height: 32px; border: 1px solid transparent; border-radius: 6px; background: var(--primary); padding: 0 12px; color: var(--on-primary); font-weight: 500; white-space: nowrap; }
.iaw-btn .ic { width: 16px; height: 16px; }
.iaw-btn.sec { border-color: var(--card-edge); background: var(--page); color: var(--ink); }
.iaw-tabs { display: flex; gap: 20px; border-bottom: 1px solid var(--line2); padding-right: 12px; color: var(--ink3); font-weight: 500; }
.iaw-tabs span { display: inline-flex; align-items: center; gap: 5px; padding: 6px 0 8px; }
.iaw-tabs .on { box-shadow: inset 0 -2px 0 var(--ink); color: var(--ink); }
.iaw-filters { display: flex; align-items: center; gap: 6px; padding-right: 12px; }
.iaw-divider { width: 1px; height: 24px; margin: 0 10px; background: var(--edge); }
.iaw-chip { display: inline-flex; align-items: center; gap: 5px; height: 24px; border: 1px solid var(--card-edge); border-radius: 4px; background: var(--page); padding: 0 8px; color: var(--ink2); font-size: 12px; font-weight: 500; white-space: nowrap; }
.iaw-watch { display: flex; align-items: center; gap: 10px; min-height: 44px; margin-right: 12px; border: 1px solid var(--card-edge); border-radius: 8px; background: var(--fill); padding: 0 12px 0 14px; }
.iaw-watch b { font-size: 14px; font-weight: 700; }
.iaw-watch-cap { color: var(--red-t); font-weight: 600; }
.iaw-watch > .ic:last-child { color: var(--ink3); }
.iaw-table { min-height: 0; overflow: hidden; border-top: 1px solid var(--line2); -webkit-mask-image: linear-gradient(90deg, #000 calc(100% - 48px), transparent); mask-image: linear-gradient(90deg, #000 calc(100% - 48px), transparent); }
.iaw-table table { width: max-content; border-collapse: collapse; table-layout: fixed; }
.iaw-table .w-ck { width: 40px; } .iaw-table .w-amt { width: 196px; } .iaw-table .w-money { width: 128px; }
.iaw-table .w-no { width: 104px; } .iaw-table .w-title { width: 214px; } .iaw-table .w-cust { width: 176px; }
.iaw-table .w-email { width: 236px; } .iaw-table .w-date { width: 104px; }
.iaw-table th, .iaw-table td { overflow: hidden; border-right: 1px solid var(--line2); border-bottom: 1px solid var(--line2); padding: 0 10px; text-align: left; text-overflow: ellipsis; white-space: nowrap; }
.iaw-table th { height: 36px; color: var(--ink3); font-size: 13px; font-weight: 500; }
.iaw-table th .ic { margin: -2px 6px 0 0; color: var(--ink3); }
.iaw-table td { height: 40px; }
.iaw-table .r { text-align: right; }
.iaw-table td.r { line-height: 1.25; }
.iaw-table td.r b, .iaw-table td.r span { display: block; }
.iaw-table td.r b { font-weight: 500; }
.iaw-table small { display: block; color: var(--ink3); font-size: 11px; }
/* The moving invoice keeps its paid-on line laid out while it is empty, and the amount is centred with a
   transform, so a payment landing never moves anything in the row. */
.iaw-table .iaw-ghost { visibility: hidden; }
.iaw-table td.r .iaw-solo { transform: translateY(calc(11px * 1.25 / 2)); }
.iaw-table .late { color: var(--red-t); font-weight: 500; }
.iaw-table small.late { font-weight: 400; }
.iaw-table .cust { font-weight: 500; }
.iaw-table .ck-cell { padding: 0 12px; }
.iaw-ck { display: block; width: 16px; height: 16px; border: 1.5px solid var(--quiet); border-radius: 3px; }
.iaw-amt { display: flex; align-items: center; gap: 8px; }
.iaw-amt b { font-weight: 400; }
.iaw-tag { display: inline-flex; flex: none; align-items: center; gap: 5px; height: 20px; border-radius: 4px; background: var(--gray-f); padding: 0 6px; color: var(--gray-t); font-size: 13px; font-style: normal; font-weight: 500; white-space: nowrap; }
.iaw-phone .iaw-tag { gap: 5px; font-weight: 500; }
.iaw-desk .iaw-tag { font-weight: 400; }
.iaw-phone .iaw-tag::before { content: ""; width: 5px; height: 5px; border-radius: 50%; background: currentColor; }
.iaw-tag.green { background: var(--green-f); color: var(--green-t); }
.iaw-tag.blue { background: var(--blue-f); color: var(--blue-t); }
.iaw-tag.red { background: var(--red-f); color: var(--red-t); }
.iaw-tag.amber { background: var(--amber-f); color: var(--amber-t); }
.iaw-foot { display: flex; align-items: center; gap: 8px; margin-top: auto; border-top: 1px solid var(--line2); padding: 8px 12px 8px 0; color: var(--ink2); }
/* The invoice that moves gets a soft wash and its tag pops each time its status changes. */
.iaw-table tr.is-focus td { animation: iaw-wash 1.4s ease-out; }
.iaw--open .iaw-table tr.is-focus td, .iaw--paid .iaw-table tr.is-focus td { animation-name: iaw-wash-b; }
@keyframes iaw-wash { from { background: var(--selected); } to { background: transparent; } }
@keyframes iaw-wash-b { from { background: var(--selected); } to { background: transparent; } }
.iaw-table tr.is-focus .iaw-tag, .iap-card.is-focus .iaw-tag { animation: iaw-pop 0.45s cubic-bezier(0.33, 1, 0.68, 1); }
.iaw--open .is-focus .iaw-tag, .iaw--paid .is-focus .iaw-tag { animation-name: iaw-pop-b; }
@keyframes iaw-pop { from { transform: scale(0.85); opacity: 0.4; } to { transform: none; opacity: 1; } }
@keyframes iaw-pop-b { from { transform: scale(0.85); opacity: 0.4; } to { transform: none; opacity: 1; } }

/* Tablet: the sidebar is collapsed, as the app's sidebar toggle leaves it. */
@container (max-width: 1080px) {
  .iaw-desk { grid-template-columns: minmax(0, 1fr); height: 540px; }
  .iaw-side { display: none; }
  .iaw-hsearch { display: none; }
  .iaw-head { padding-left: 14px; }
  /* Invoice # and Title switched off in Columns, so the client stays in view. */
  .iaw-table .c-no, .iaw-table .c-title { display: none; }
  .iaw-table .w-amt { width: 180px; } .iaw-table .w-money { width: 116px; }
}

/* ---------- phone (one app point = 1/390 of the width, text at the app's default 125%) ---------- */
.iaw-phone { display: none; }
@container (max-width: 600px) {
  .iaw-desk { display: none; }
  .iaw-phone {
    --u: calc(100cqi / 390);
    --t: calc(var(--u) * 1.25);
    position: relative;
    display: block;
    height: calc(var(--u) * 700);
    overflow: hidden;
    border: 1px solid var(--card-edge);
    border-radius: 10px;
    background: var(--page);
    font-size: calc(var(--t) * 13);
  }
  .iaw-phone .ic { width: calc(var(--t) * 16); height: calc(var(--t) * 16); }
  .iap-bar { display: flex; justify-content: flex-end; gap: calc(var(--u) * 8); padding: calc(var(--u) * 10) calc(var(--u) * 16) calc(var(--u) * 8); }
  .iap-icbtn { display: grid; place-items: center; width: calc(var(--u) * 35); height: calc(var(--u) * 35); border: 1px solid var(--card-edge); border-radius: calc(var(--u) * 8); color: var(--ink2); }
  .iap-add { display: inline-flex; align-items: center; gap: calc(var(--u) * 5); height: calc(var(--u) * 35); border-radius: calc(var(--u) * 6); background: var(--primary); padding: 0 calc(var(--u) * 14); color: var(--on-primary); font-weight: 500; }
  .iap-body { display: grid; gap: calc(var(--u) * 10); padding: 0 calc(var(--u) * 16); }
  .iap-search { display: flex; align-items: center; gap: calc(var(--u) * 8); height: calc(var(--t) * 34); border: 1px solid var(--card-edge); border-radius: calc(var(--u) * 6); padding: 0 calc(var(--u) * 10); color: var(--ink3); }
  .iap-seg { display: grid; grid-template-columns: 1fr 1fr; gap: calc(var(--u) * 3); border-radius: calc(var(--u) * 6); background: var(--selected); padding: calc(var(--u) * 3); }
  .iap-seg span { display: flex; align-items: center; justify-content: center; gap: calc(var(--u) * 5); height: calc(var(--t) * 26); border-radius: calc(var(--u) * 4); color: var(--ink2); font-weight: 500; }
  .iap-seg .on { background: var(--page); box-shadow: 0 1px 2px rgb(0 0 0 / 0.08); color: var(--ink); }
  .iap-rail { display: flex; align-items: center; gap: calc(var(--u) * 6); }
  .iap-chip { display: inline-flex; align-items: center; gap: calc(var(--u) * 5); height: calc(var(--t) * 24); border: 1px solid var(--card-edge); border-radius: calc(var(--u) * 4); padding: 0 calc(var(--u) * 8); color: var(--ink2); font-size: calc(var(--t) * 12); font-weight: 500; }
  .iap-chip .ic { width: calc(var(--t) * 14); height: calc(var(--t) * 14); }
  .iap-more { display: grid; place-items: center; width: calc(var(--u) * 40); height: calc(var(--t) * 24); color: var(--ink2); }
  .iap-watch { display: flex; align-items: center; gap: calc(var(--u) * 10); border: 1px solid var(--card-edge); border-radius: calc(var(--u) * 8); padding: calc(var(--u) * 10) calc(var(--u) * 12); }
  .iap-watch > span { display: grid; flex: 1; min-width: 0; }
  .iap-watch b { font-size: calc(var(--t) * 14); font-weight: 700; }
  .iap-watch em { overflow: hidden; color: var(--red-t); font-style: normal; font-weight: 600; text-overflow: ellipsis; white-space: nowrap; }
  .iap-watch > .ic:last-child { color: var(--ink3); }
  .iap-card { position: relative; overflow: hidden; margin: calc(var(--u) * 6) 0 calc(var(--u) * 2); border: 0.6px solid var(--card-line); border-radius: 7px; background: var(--page); box-shadow: 0 5px 12px rgb(0 0 0 / 0.025); }
  .dark .iap-card { box-shadow: 0 5px 12px rgb(0 0 0 / 0.16); }
  .iap-card::before { content: ""; position: absolute; inset: 0 auto 0 0; width: 5px; background: var(--stripe, #6b7c93); transition: background 0.4s ease; }
  .iap-card.is-paid::before { background: linear-gradient(160deg, #e4c879 0%, #b88700 45%, #f0d999 70%, #b88700 100%); }
  .iap-card-in { padding: calc(var(--u) * 15) calc(var(--u) * 16) calc(var(--u) * 12) calc(var(--u) * 21); color: var(--card-ink); }
  .iap-card-head { display: flex; align-items: flex-start; gap: calc(var(--u) * 10); }
  .iap-card-head b { flex: 1; min-width: 0; overflow: hidden; font-size: calc(var(--t) * 20); font-weight: 700; line-height: 1.15; text-overflow: ellipsis; white-space: nowrap; }
  .iap-acts { display: flex; align-items: center; gap: calc(var(--u) * 7); }
  .iap-dots { display: grid; place-items: center; width: calc(var(--t) * 26); height: calc(var(--t) * 26); border: 1px solid var(--card-line); border-radius: calc(var(--u) * 6); color: var(--card-muted); }
  .iaw-phone .iaw-tag { height: calc(var(--t) * 20); font-size: calc(var(--t) * 12); }
  .iap-client { margin: calc(var(--u) * 9) 0 0; font-size: calc(var(--t) * 16); }
  .iap-factsets { display: grid; margin: calc(var(--u) * 12) 0 0; }
  .iap-facts { display: flex; flex-wrap: wrap; align-content: flex-start; justify-content: space-between; gap: calc(var(--u) * 10) calc(var(--u) * 18); grid-area: 1 / 1; min-height: calc(var(--t) * 12 * 1.3 * 2 + var(--t) * 16 * 1.3 * 2 + var(--u) * 18); margin: 0; }
  .iap-facts.is-off { visibility: hidden; }
  .iap-facts dt { color: var(--card-muted); font-size: calc(var(--t) * 12); }
  .iap-facts dd { margin: calc(var(--u) * 4) 0 0; font-size: calc(var(--t) * 16); font-weight: 700; }
  .iap-facts dd.ok { color: var(--card-ok); }
  .iap-facts dd.warn { color: var(--card-warn); }
  .iap-facts dd.bad { color: var(--card-bad); }
  .iap-card-foot { display: grid; gap: calc(var(--u) * 9); border-top: 0.6px solid var(--card-line); padding: calc(var(--u) * 9) calc(var(--u) * 11) calc(var(--u) * 9) calc(var(--u) * 16); }
  .iap-opened { display: inline-flex; align-items: center; gap: calc(var(--u) * 6); justify-self: start; height: calc(var(--u) * 32); border: 0.6px solid var(--card-line); border-radius: calc(var(--u) * 6); padding: 0 calc(var(--u) * 10); color: var(--card-muted); font-size: calc(var(--t) * 12); }
  .iap-opened b { color: var(--card-ink); font-weight: 600; }
  .iap-btns { display: flex; justify-content: flex-end; gap: calc(var(--u) * 7); }
  .iap-btn { display: inline-flex; align-items: center; gap: calc(var(--u) * 5); height: calc(var(--u) * 32); border: 0.6px solid var(--card-line); border-radius: calc(var(--u) * 6); padding: 0 calc(var(--u) * 10); color: var(--card-ink); font-size: calc(var(--t) * 12); font-weight: 600; }
  .iap-btn.primary { border-color: transparent; background: var(--primary); color: var(--on-primary); }
  .iaw-phone .iap-btn .ic, .iaw-phone .iap-opened .ic { width: calc(var(--t) * 14); height: calc(var(--t) * 14); }
  .iap-nav { position: absolute; z-index: 3; right: calc(var(--u) * 12); bottom: calc(var(--u) * 14); left: calc(var(--u) * 12); display: flex; align-items: center; gap: calc(var(--u) * 4); border: 1px solid var(--card-edge); border-radius: 10px; background: var(--page); padding: calc(var(--u) * 6); box-shadow: 0 8px 24px rgb(0 0 0 / 0.12), 0 -14px 18px -6px var(--page); }
  /* The strip under the floating bar is the page, so cards never show beneath it. */
  .iap-nav::after { content: ""; position: absolute; right: calc(var(--u) * -13); bottom: calc(var(--u) * -15); left: calc(var(--u) * -13); height: calc(var(--u) * 14); background: var(--page); }
  .iap-nav > span:not(.iap-navmore) { display: grid; flex: 1; justify-items: center; gap: calc(var(--u) * 2); color: var(--ink3); font-size: calc(var(--u) * 10.5); white-space: nowrap; }
  .iap-nav .on { color: var(--ink); font-weight: 600; }
  .iap-nav .ic { width: calc(var(--u) * 22); height: calc(var(--u) * 22); }
  .iap-navmore { display: grid; flex: none; place-items: center; width: calc(var(--u) * 44); height: calc(var(--u) * 44); border-radius: 8px; background: var(--primary); color: var(--on-primary); }
}
@media (prefers-reduced-motion: reduce) { .iaw * { animation: none !important; transition: none !important; } }
</style>
